import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, computed, inject, signal } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { ThemeService } from '../../../../services/theme/theme.service';
import { getTurnstileApi, TurnstileScriptService } from '../../../onboarding-v2/service/turnstile-script/turnstile-script.service';
import { brandInitial } from '../../../onboarding-value-first/services/value-first-onboarding.types';
import { ValueFirstAgencyService } from '../../services/value-first-agency.service';
import { AGENCY_TRIAL_LINE } from '../../services/value-first-agency.types';

@Component({
  selector: 'vfa-account-step',
  templateUrl: './account-step.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VfaAccountStep implements AfterViewInit, OnDestroy {
  readonly onboarding = inject(ValueFirstAgencyService);
  private readonly turnstileScript = inject(TurnstileScriptService);
  private readonly theme = inject(ThemeService);

  @ViewChild('turnstileContainer') turnstileContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('honeypotInput') honeypotInput?: ElementRef<HTMLInputElement>;

  private turnstileWidgetId: string | null = null;
  readonly passwordVisible = signal(false);

  readonly priceLine = computed(() => {
    const currency = this.onboarding.displayCurrency();
    return currency ? AGENCY_TRIAL_LINE[currency] : '14-day free trial. 3 brands included.';
  });

  initial(): string {
    return brandInitial(this.onboarding.agencyName());
  }

  ngAfterViewInit(): void {
    void this.initTurnstile();
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId) {
      getTurnstileApi()?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }

  onFirstName(event: Event): void {
    this.onboarding.setName((event.target as HTMLInputElement).value, this.onboarding.draft().lastName);
  }

  onLastName(event: Event): void {
    this.onboarding.setName(this.onboarding.draft().firstName, (event.target as HTMLInputElement).value);
  }

  onEmail(event: Event): void {
    this.onboarding.setEmail((event.target as HTMLInputElement).value);
  }

  onPassword(event: Event): void {
    this.onboarding.setPassword((event.target as HTMLInputElement).value);
  }

  togglePassword(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  submit(event: Event): void {
    event.preventDefault();
    if (this.onboarding.draft().authMode === 'google') {
      void this.onboarding.completeGoogleAccount();
      return;
    }
    void this.onboarding.sendCode(this.honeypotInput?.nativeElement.value ?? '');
  }

  google(): void {
    void this.onboarding.continueWithGoogle();
  }

  private async initTurnstile(): Promise<void> {
    try {
      await this.turnstileScript.load();
    } catch {
      return;
    }
    const turnstile = getTurnstileApi();
    if (!turnstile || !this.turnstileContainer?.nativeElement) {
      return;
    }
    this.turnstileWidgetId = turnstile.render(this.turnstileContainer.nativeElement, {
      sitekey: environment.cloudflare.turnstileSiteKey,
      callback: (token: string) => this.onboarding.setTurnstileToken(token),
      'expired-callback': () => this.onboarding.setTurnstileToken(null),
      'error-callback': () => this.onboarding.setTurnstileToken(null),
      theme: this.theme.isDarkMode() ? 'dark' : 'light',
      appearance: 'interaction-only',
    });
  }
}
